import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { API_BASE } from '../config';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { SkeletonList } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import {
  MessageSquare,
  Send,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  User,
  Bot,
  Sparkles,
  Clock,
  Brain,
  Briefcase,
  Target,
  Award,
  X,
  Plus,
  RefreshCw,
  Wand2,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────

interface InterviewSession {
  id: number;
  session_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  message_count: number;
  skills_found: number;
}

interface InterviewMessage {
  role: 'system' | 'assistant' | 'user';
  content: string;
  timestamp: string;
}

interface WizardData {
  current_title?: string;
  target_roles?: string[];
  industries?: string[];
  remote_preference?: string;
  salary_min?: number;
  salary_max?: number;
  validated_skills?: Array<{ name: string; proficiency: string; confirmed: boolean }>;
  largest_team?: string;
  key_achievement?: string;
  leadership_strength?: string;
}

interface Suggestions {
  profile: {
    current_title: string;
    target_titles: string[];
    industries: string[];
    remote_preference: string;
    salary_min: number | null;
    salary_max: number | null;
  };
  resume_keywords: string[];
  market_industries: string[];
  market_roles: string[];
}

type Phase = 'session_list' | 'wizard_step1' | 'wizard_step2' | 'wizard_step3' | 'deep_dive';

// ─── Component ──────────────────────────────────────────

const Interview: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('session_list');
  const [sessions, setSessions] = useState<InterviewSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<InterviewMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [wizardData, setWizardData] = useState<WizardData>({});
  const [newTargetRole, setNewTargetRole] = useState('');
  const [newIndustry, setNewIndustry] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [resumingSession, setResumingSession] = useState<number | null>(null);
  const { toast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.get<{ sessions?: typeof sessions }>(
        '/api/intelligence/interview/sessions',
        { retry: 2 },
      );
      setSessions(data.sessions || []);
    } catch (e) {
      setLoadError(errorText(e, 'Failed to load sessions'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/interview/suggestions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data);
      }
    } catch { /* skip */ }
    setLoadingSuggestions(false);
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startNewSession = async (sessionType: string = 'onboarding') => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/interview/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_type: sessionType }),
      });
      if (res.ok) {
        const data = await res.json();
        setActiveSessionId(data.sessionId);
        setMessages([data.firstMessage]);
        setWizardData({});

        // For onboarding, fetch suggestions and show wizard
        if (sessionType === 'onboarding') {
          fetchSuggestions();
          setPhase('wizard_step1');
        } else {
          // Skip wizard for non-onboarding types
          setPhase('deep_dive');
        }
      }
    } catch { /* skip */ }
  };

  const resumeSession = async (session: InterviewSession) => {
    setResumingSession(session.id);
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/interview/${session.id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setActiveSessionId(session.id);
        setMessages(data.messages || []);
        setWizardData(data.wizard_data || {});

        // Determine what phase to show
        if (data.status === 'completed') {
          // View completed session in read-only chat
          setPhase('deep_dive');
        } else if (data.wizard_data && Object.keys(data.wizard_data).length > 0) {
          // Has wizard data — check how far they got
          if (data.wizard_data.leadership_strength || data.wizard_data.key_achievement) {
            setPhase('deep_dive'); // Completed step 2, go to chat
          } else if (data.wizard_data.current_title || data.wizard_data.target_roles?.length) {
            fetchSuggestions();
            setPhase('wizard_step2'); // Completed step 1
          } else {
            fetchSuggestions();
            setPhase('wizard_step1');
          }
        } else {
          // No wizard data — either fresh or non-onboarding
          if (session.session_type === 'onboarding' && session.message_count <= 1) {
            fetchSuggestions();
            setPhase('wizard_step1');
          } else {
            setPhase('deep_dive');
          }
        }
      }
    } catch {
      toast.error('Failed to load session');
    }
    setResumingSession(null);
  };

  const autoPopulateFromProfile = () => {
    if (!suggestions) return;
    setWizardData((prev) => ({
      ...prev,
      current_title: prev.current_title || suggestions.profile.current_title || '',
      target_roles: prev.target_roles?.length ? prev.target_roles : suggestions.profile.target_titles.length ? [...suggestions.profile.target_titles] : [],
      industries: prev.industries?.length ? prev.industries : suggestions.profile.industries.length ? [...suggestions.profile.industries] : [],
      remote_preference: prev.remote_preference || suggestions.profile.remote_preference || 'hybrid',
      salary_min: prev.salary_min || suggestions.profile.salary_min || undefined,
      salary_max: prev.salary_max || suggestions.profile.salary_max || undefined,
    }));
  };

  const sendMessage = async (msg?: string) => {
    const text = msg || input.trim();
    if (!text || !activeSessionId) return;

    setSending(true);
    setInput('');

    const userMsg: InterviewMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const res = await fetch(`${API_BASE}/api/intelligence/interview/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: activeSessionId,
          message: text,
          wizard_data: wizardData,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages((prev) => [...prev, data.response]);
      }
    } catch { /* skip */ }
    setSending(false);
  };

  const completeSession = async () => {
    if (!activeSessionId) return;
    try {
      await fetch(`${API_BASE}/api/intelligence/interview/${activeSessionId}/complete`, {
        method: 'POST',
        credentials: 'include',
      });
      setPhase('session_list');
      setActiveSessionId(null);
      setMessages([]);
      fetchSessions();
    } catch { /* skip */ }
  };

  const advanceWizard = () => {
    if (phase === 'wizard_step1') {
      const summary = `My current title is "${wizardData.current_title || 'not specified'}". I'm targeting roles: ${(wizardData.target_roles || []).join(', ') || 'not specified'}. I prefer ${wizardData.remote_preference || 'hybrid'} work. Industries: ${(wizardData.industries || []).join(', ') || 'any'}.`;
      sendMessage(summary);
      setPhase('wizard_step2');
    } else if (phase === 'wizard_step2') {
      const summary = `For achievements: largest team managed: ${wizardData.largest_team || 'N/A'}. Key achievement: ${wizardData.key_achievement || 'N/A'}. Leadership strength: ${wizardData.leadership_strength || 'N/A'}.`;
      sendMessage(summary);
      setPhase('wizard_step3');
    } else if (phase === 'wizard_step3') {
      setPhase('deep_dive');
    }
  };

  const goBackWizard = () => {
    if (phase === 'wizard_step2') setPhase('wizard_step1');
    else if (phase === 'wizard_step3') setPhase('wizard_step2');
  };

  const addTargetRole = () => {
    if (newTargetRole.trim()) {
      setWizardData((prev) => ({
        ...prev,
        target_roles: [...(prev.target_roles || []), newTargetRole.trim()],
      }));
      setNewTargetRole('');
    }
  };

  const addIndustry = () => {
    if (newIndustry.trim()) {
      setWizardData((prev) => ({
        ...prev,
        industries: [...(prev.industries || []), newIndustry.trim()],
      }));
      setNewIndustry('');
    }
  };

  const stepNumber = phase === 'wizard_step1' ? 1 : phase === 'wizard_step2' ? 2 : phase === 'wizard_step3' ? 3 : 0;

  // ─── Session List ───────────────────────────────────

  if (phase === 'session_list') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageSquare className="w-7 h-7 text-indigo-600" />
              AI Career Interview
            </h1>
            <p className="text-gray-500 dark:text-slate-400 mt-1">Build your skill profile through structured questions and AI conversation</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => startNewSession('onboarding')}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            New Interview
          </motion.button>
        </div>

        {/* Session Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { type: 'onboarding', label: 'Full Onboarding', icon: Sparkles, description: 'Wizard + deep dive conversation', color: 'text-indigo-500' },
            { type: 'deep_dive', label: 'Deep Dive', icon: Brain, description: 'Conversational skill exploration', color: 'text-purple-500' },
            { type: 'skill_validation', label: 'Skill Validation', icon: Target, description: 'Verify your skill proficiencies', color: 'text-green-500' },
            { type: 'career_goals', label: 'Career Goals', icon: Award, description: 'Define your trajectory', color: 'text-amber-500' },
          ].map(({ type, label, icon: Icon, description, color }) => (
            <motion.button
              key={type}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => startNewSession(type)}
              className="p-4 bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow text-left"
            >
              <Icon className={`w-8 h-8 ${color} mb-2`} />
              <h3 className="font-semibold text-gray-900 dark:text-white">{label}</h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{description}</p>
            </motion.button>
          ))}
        </div>

        {/* Past Sessions */}
        {sessions.length > 0 && (
          <div className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Past Sessions</h2>
            <div className="space-y-2">
              {sessions.map((s) => (
                <motion.button
                  key={s.id}
                  whileHover={{ scale: 1.005 }}
                  onClick={() => resumeSession(s)}
                  disabled={resumingSession === s.id}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-xl transition-colors text-left disabled:opacity-60"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${s.status === 'completed' ? 'bg-green-400' : 'bg-yellow-400'}`} />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{s.session_type.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                        <Clock className="w-3 h-3" />
                        {new Date(s.started_at).toLocaleDateString()}
                        <span>{s.message_count} messages</span>
                        <span>{s.skills_found} skills found</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {resumingSession === s.id && <RefreshCw className="w-3 h-3 animate-spin text-gray-400 dark:text-slate-500" />}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      s.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {s.status === 'completed' ? 'completed' : 'resume'}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <SkeletonList rows={3} />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchSessions} />
        ) : null}
      </div>
    );
  }

  // ─── Wizard + Chat View ─────────────────────────────

  return (
    <div className="flex gap-6 h-[calc(100vh-140px)]">
      {/* Left Panel: Wizard or Chat */}
      <div className="flex-1 flex flex-col bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {phase === 'deep_dive' ? 'AI Deep Dive' : `Step ${stepNumber} of 3`}
            </h2>
            {/* Step indicator dots */}
            {stepNumber > 0 && (
              <div className="flex items-center gap-1.5 ml-2">
                {[1, 2, 3].map((s) => (
                  <div key={s} className={`w-2 h-2 rounded-full transition-colors ${
                    s === stepNumber ? 'bg-indigo-600' : s < stepNumber ? 'bg-indigo-300' : 'bg-gray-200 dark:bg-slate-700'
                  }`} />
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {phase === 'deep_dive' && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={completeSession}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700"
              >
                <CheckCircle className="w-3 h-3" /> Complete
              </motion.button>
            )}
            <button
              onClick={() => { setPhase('session_list'); setActiveSessionId(null); setMessages([]); }}
              className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Wizard Step 1: Career Basics */}
        {phase === 'wizard_step1' && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-500" />
                Career Basics
              </h3>
              {suggestions && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={autoPopulateFromProfile}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto-fill from profile
                </motion.button>
              )}
              {!suggestions && loadingSuggestions && (
                <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-slate-500">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Loading suggestions...
                </span>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Current Title</label>
                <input
                  type="text"
                  value={wizardData.current_title || ''}
                  onChange={(e) => setWizardData((p) => ({ ...p, current_title: e.target.value }))}
                  placeholder="e.g., Senior Engineering Manager"
                  className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Target Roles</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTargetRole}
                    onChange={(e) => setNewTargetRole(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTargetRole())}
                    placeholder="e.g., Director of Engineering"
                    className="flex-1 border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-800 dark:text-white"
                    list="role-suggestions"
                  />
                  <button onClick={addTargetRole} className="px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-xl text-sm hover:bg-indigo-200 dark:hover:bg-indigo-900/50">Add</button>
                </div>
                {suggestions && suggestions.market_roles.length > 0 && (
                  <datalist id="role-suggestions">
                    {suggestions.market_roles.map((r, i) => <option key={i} value={r} />)}
                  </datalist>
                )}
                <div className="flex flex-wrap gap-1">
                  {(wizardData.target_roles || []).map((r, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center gap-1">
                      {r}
                      <button onClick={() => setWizardData((p) => ({ ...p, target_roles: p.target_roles?.filter((_, j) => j !== i) }))} className="text-indigo-400 dark:text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-300">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {/* Market role suggestions as clickable chips */}
                {suggestions && suggestions.market_roles.length > 0 && (wizardData.target_roles || []).length === 0 && (
                  <div className="mt-2">
                    <span className="text-xs text-gray-400 dark:text-slate-500 mr-1">Trending roles:</span>
                    {suggestions.market_roles.slice(0, 6).map((r, i) => (
                      <button
                        key={i}
                        onClick={() => setWizardData((p) => ({ ...p, target_roles: [...(p.target_roles || []), r] }))}
                        className="text-xs px-2 py-0.5 mr-1 mb-1 bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-slate-400 rounded-full border border-gray-200 dark:border-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
                      >
                        + {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Industries</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newIndustry}
                    onChange={(e) => setNewIndustry(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addIndustry())}
                    placeholder="e.g., AI, Fintech"
                    className="flex-1 border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-slate-800 dark:text-white"
                    list="industry-suggestions"
                  />
                  <button onClick={addIndustry} className="px-3 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-xl text-sm hover:bg-indigo-200 dark:hover:bg-indigo-900/50">Add</button>
                </div>
                {suggestions && suggestions.market_industries.length > 0 && (
                  <datalist id="industry-suggestions">
                    {suggestions.market_industries.map((r, i) => <option key={i} value={r} />)}
                  </datalist>
                )}
                <div className="flex flex-wrap gap-1">
                  {(wizardData.industries || []).map((r, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-full flex items-center gap-1">
                      {r}
                      <button onClick={() => setWizardData((p) => ({ ...p, industries: p.industries?.filter((_, j) => j !== i) }))} className="text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                {suggestions && suggestions.market_industries.length > 0 && (wizardData.industries || []).length === 0 && (
                  <div className="mt-2">
                    <span className="text-xs text-gray-400 dark:text-slate-500 mr-1">From job market:</span>
                    {suggestions.market_industries.slice(0, 8).map((r, i) => (
                      <button
                        key={i}
                        onClick={() => setWizardData((p) => ({ ...p, industries: [...(p.industries || []), r] }))}
                        className="text-xs px-2 py-0.5 mr-1 mb-1 bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-slate-400 rounded-full border border-gray-200 dark:border-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
                      >
                        + {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Remote Preference</label>
                <select
                  value={wizardData.remote_preference || 'hybrid'}
                  onChange={(e) => setWizardData((p) => ({ ...p, remote_preference: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                >
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">On-site</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Min Salary</label>
                  <input
                    type="number"
                    value={wizardData.salary_min || ''}
                    onChange={(e) => setWizardData((p) => ({ ...p, salary_min: parseInt(e.target.value, 10) || 0 }))}
                    placeholder="150000"
                    className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Max Salary</label>
                  <input
                    type="number"
                    value={wizardData.salary_max || ''}
                    onChange={(e) => setWizardData((p) => ({ ...p, salary_max: parseInt(e.target.value, 10) || 0 }))}
                    placeholder="250000"
                    className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              {/* Resume keyword highlights */}
              {suggestions && suggestions.resume_keywords.length > 0 && (
                <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-100 dark:border-primary-800">
                  <div className="text-xs font-medium text-primary-700 dark:text-primary-400 mb-1.5 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Your resume keywords
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {suggestions.resume_keywords.slice(0, 20).map((kw, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-white dark:bg-slate-800 text-primary-600 dark:text-primary-400 rounded-full border border-primary-200 dark:border-primary-800">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="mt-6 flex items-center justify-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={advanceWizard}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        )}

        {/* Wizard Step 2: Key Achievements */}
        {phase === 'wizard_step2' && (
          <div className="flex-1 overflow-y-auto p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-indigo-500" />
              Key Achievements
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Largest team you've managed?</label>
                <input
                  type="text"
                  value={wizardData.largest_team || ''}
                  onChange={(e) => setWizardData((p) => ({ ...p, largest_team: e.target.value }))}
                  placeholder="e.g., 25 engineers across 3 teams"
                  className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Most significant achievement?</label>
                <textarea
                  value={wizardData.key_achievement || ''}
                  onChange={(e) => setWizardData((p) => ({ ...p, key_achievement: e.target.value }))}
                  placeholder="e.g., Led migration to microservices that reduced latency by 60% and saved $2M/year"
                  rows={3}
                  className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">What's your leadership superpower?</label>
                <textarea
                  value={wizardData.leadership_strength || ''}
                  onChange={(e) => setWizardData((p) => ({ ...p, leadership_strength: e.target.value }))}
                  placeholder="e.g., Building high-trust teams that ship fast — I've turned around 3 underperforming teams"
                  rows={3}
                  className="w-full border border-gray-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            {/* Navigation: Previous + Continue */}
            <div className="mt-6 flex items-center justify-between">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={goBackWizard}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={advanceWizard}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
              >
                Continue to AI Deep Dive <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        )}

        {/* Wizard Step 3: Transition to AI */}
        {phase === 'wizard_step3' && (
          <div className="flex-1 overflow-y-auto p-6 text-center flex flex-col items-center justify-center">
            <Brain className="w-16 h-16 text-indigo-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Ready for AI Deep Dive</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              Our AI will now ask you progressively deeper questions to discover hidden strengths, validate your skills, and map your career trajectory.
            </p>
            <div className="flex items-center gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={goBackWizard}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-slate-400 bg-gray-100 dark:bg-slate-800 rounded-xl hover:bg-gray-200 dark:hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={advanceWizard}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
              >
                <Sparkles className="w-4 h-4" />
                Start Conversation
              </motion.button>
            </div>
          </div>
        )}

        {/* Deep Dive Chat */}
        {phase === 'deep_dive' && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, i) => (
                msg.role !== 'system' && (
                  <div key={i} className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-purple-100 dark:bg-purple-900/30'
                    }`}>
                      {msg.role === 'user' ? <User className="w-4 h-4 text-indigo-600" /> : <Bot className="w-4 h-4 text-purple-600" />}
                    </div>
                    <div className={`max-w-[70%] rounded-xl px-4 py-2.5 text-sm ${
                      msg.role === 'user'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                )
              ))}
              {sending && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="bg-gray-100 dark:bg-slate-800 rounded-xl px-4 py-2.5 text-sm text-gray-400 dark:text-slate-500">
                    Thinking...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-slate-800">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type your response..."
                  disabled={sending}
                  className="flex-1 border border-gray-300 dark:border-slate-700 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-slate-800 dark:text-white"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => sendMessage()}
                  disabled={sending || !input.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right Panel: Profile Preview */}
      <div className="w-80 bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-4 overflow-y-auto hidden lg:block">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-600" />
          Profile Preview
        </h3>

        {wizardData.current_title && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Current Title</div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">{wizardData.current_title}</div>
          </div>
        )}

        {(wizardData.target_roles || []).length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium mb-1">Target Roles</div>
            <div className="flex flex-wrap gap-1">
              {wizardData.target_roles!.map((r, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full">{r}</span>
              ))}
            </div>
          </div>
        )}

        {(wizardData.industries || []).length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium mb-1">Industries</div>
            <div className="flex flex-wrap gap-1">
              {wizardData.industries!.map((r, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-full">{r}</span>
              ))}
            </div>
          </div>
        )}

        {wizardData.remote_preference && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Remote Preference</div>
            <div className="text-sm text-gray-700 dark:text-slate-300 capitalize">{wizardData.remote_preference}</div>
          </div>
        )}

        {(wizardData.salary_min || wizardData.salary_max) && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Salary Range</div>
            <div className="text-sm text-gray-700 dark:text-slate-300">
              {wizardData.salary_min ? `$${wizardData.salary_min.toLocaleString()}` : '?'}
              {' — '}
              {wizardData.salary_max ? `$${wizardData.salary_max.toLocaleString()}` : '?'}
            </div>
          </div>
        )}

        {wizardData.largest_team && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Largest Team</div>
            <div className="text-sm text-gray-700 dark:text-slate-300">{wizardData.largest_team}</div>
          </div>
        )}

        {wizardData.key_achievement && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Key Achievement</div>
            <div className="text-sm text-gray-700 dark:text-slate-300">{wizardData.key_achievement}</div>
          </div>
        )}

        {wizardData.leadership_strength && (
          <div className="mb-4">
            <div className="text-xs text-gray-500 dark:text-slate-400 uppercase font-medium">Leadership Strength</div>
            <div className="text-sm text-gray-700 dark:text-slate-300">{wizardData.leadership_strength}</div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-slate-800">
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {messages.filter((m) => m.role === 'user').length} responses recorded
          </div>
        </div>
      </div>
    </div>
  );
};

export default Interview;
